<template>
  <div class="container mt-4">
    <div class="row">
      <div class="col-12">
        <b-card>
          <div class="invoice-nav px-4">
            <div
              class="cursor-pointer"
              style="color: #3f77f5"
              @click="$router.push({ name: 'Billing' })"
            >
              <i
                class="fas fa-arrow-left"
                style="color: #3f77f5; font-size: 15px"
              ></i>
              Back to invoices
            </div>
            <div>
              <span> Make a payment </span>
            </div>
            <div class="w-25 d-hidden"></div>
          </div>
          <b-form-group
            :state="formErrors.card.length == 0 ? null : false"
            :invalid-feedback="formErrors.card.join(', ')"
          >
            <div class="payment-method-container py-5">
              <patient-payment-methods-pay
                ref="creditCardSelector"
                :patientId="patientId"
                v-model="card"
              />
            </div>
          </b-form-group>
          <div>
            <div class="d-flex justify-content-center mt-3">
              <b-button
                @click="submitPayment"
                class="text-white"
                :disabled="loading || !card"
                variant="success"
                size="lg"
              >
                <b-spinner
                  v-if="loading"
                  style="width: 2rem; height: 2rem"
                  class="text-white"
                  label="Spinning"
                ></b-spinner>
                <span v-else>
                  Pay {{ toCurrencyValue(totalAmount) }}</span
                ></b-button
              >
            </div>
          </div>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { defineComponent } from "vue";
import PatientPaymentMethodsPay from "./PatientPaymentMethodsPay.vue";
import paymentServer from "../../server/payment";
import { billingServer } from "../../server/billing";
import moment from "moment";
export default defineComponent({
  props: {
    invoiceId: {
      type: String,
      required: false
    },
    invoices: {
      type: Array,
      required: false
    }
  },
  components: { PatientPaymentMethodsPay },
  data() {
    return {
      card: null,
      invoice: null,
      loading: false,
      formErrors: {
        card: []
      }
    };
  },
  async mounted() {
    if (this.invoiceId) await this.fetchInvoiceDetails();
  },
  computed: {
    ...mapState("session", {
      patientId: state => state.user.PatientId,
      patient: state => state.user.Patient
    }),
    totalAmount() {
      if (this.invoice !== null) {
        return this.invoice.totalAmount - this.invoice.amountPaid;
      } else if (this.invoices !== null) {
        return (
          this.invoices &&
          this.invoices.reduce(
            (acc, invoice) => acc + (invoice.totalAmount - invoice.amountPaid),
            0
          )
        );
      }
    }
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    validatePaymentSubmit() {
      let withoutErrors = true;

      this.formErrors.card = [];
      if (this.isCardExpired()) {
        this.formErrors.card.push("The card has already expired");
        withoutErrors = false;
      }

      if (!this.card.ccInfoKey) {
        this.formErrors.card.push("Select a card or create a new one.");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    isCardExpired() {
      if (!this.card) return false;

      const currentYear = parseInt(
        moment()
          .year()
          .toString()
          .slice(-2)
      );
      const currentMonth = moment().month() + 1;

      const cardExpirationDate = this.card.expDate.trim();
      const cardExpirationMonth = parseInt(cardExpirationDate.slice(0, 2));
      const cardExpirationYear = parseInt(
        cardExpirationDate.slice(2, cardExpirationDate.length)
      );

      const isExpirationYearBelow = cardExpirationYear < currentYear;
      const isSameYear = cardExpirationYear === currentYear;
      const isExpirationMonthBelow = cardExpirationMonth < currentMonth;

      return isExpirationYearBelow || (isSameYear && isExpirationMonthBelow);
    },
    async submitPayment() {
      this.loading = true;
      let newCardValidation = true;
      if (this.card.ccInfoKey === "newCard") {
        newCardValidation = await this.$refs.creditCardSelector.submitCard();
      }
      if (newCardValidation === false) {
        this.loading = false;
        return;
      }
      if (this.validatePaymentSubmit()) {
        const paymentData = {
          patientId: this.patientId,
          type: "Payment",
          method: "Online",
          paymentDate: new Date(),
          totalAmount: this.totalAmount,
          pendingAllocatedAmount: 0,
          refundableAmount: 0,
          invoices:
            this.invoices && this.invoices.length > 0
              ? this.invoices.map(invoice => ({
                  ...invoice,
                  amount: parseFloat(invoice.totalAmount - invoice.amountPaid),
                  refNumber: invoice.refNumber
                }))
              : [
                  {
                    id: this.invoiceId,
                    amount: parseFloat(this.totalAmount),
                    refNumber: this.invoice.refNumber
                  }
                ],
          cardInfokey: this.card.ccInfoKey
        };
        this.$dialog
          .confirm(
            {
              title: `Are you sure you want to submit a payment for  <strong>${this.toCurrencyValue(
                this.totalAmount
              )}</strong>?`
            },
            {
              view: "confirm",
              options: [{ value: true, label: "Yes" }]
            }
          )
          .then(async () => {
            this.loading = true;
            const response = await paymentServer.create(paymentData);
            if (response.data.id) {
              await this.showSuccessPayment();
              this.loading = false;
              this.$router.push({
                name: "Billing"
              });
            }
            this.loading = false;
          })
          .catch(async err => {
            const data = err.response.data;
            if (data.response.result === "110") {
              await this.$dialog
                .confirm(
                  {
                    title: "Posible duplicate payment, do you want to continue?"
                  },
                  {
                    view: "confirm",
                    options: [{ value: true, label: "Yes" }]
                  }
                )
                .then(async () => {
                  this.loading = true;
                  const response = await paymentServer.create({
                    ...paymentData,
                    forceTransaction: "True"
                  });
                  if (response.data.id) {
                    this.loading = false;
                    this.showSuccessPayment();
                  }
                })
                .catch(err => {
                  this.loading = false;
                  const data = err.response.data;
                  this.showErrorPayment(data);
                });
            } else {
              this.loading = false;
              this.showErrorPayment(data);
            }
          });
      } else {
        this.loading = false;
      }
    },
    async showSuccessPayment() {
      try {
        await this.$dialog.confirm(
          {},
          {
            view: "payment-result",
            data: {
              patient: this.formatClient(
                this.patient.FirstName,
                this.patient.MiddleName,
                this.patient.PreferredName,
                this.patient.LastName
              ),
              amount: this.toCurrencyValue(this.totalAmount),
              method: "Credit/Debit Card"
            },
            success: true
          }
        );
      } catch (error) {
        this.$router.push({
          name: this.$route.params.fromPaywall ? "Appointments" : "Billing",
          params: {
            reload: true
          }
        });
      }
    },

    showErrorPayment(data) {
      this.$dialog.confirm(
        {},
        {
          view: "payment-result",
          data: data,
          success: false
        }
      );
    },
    async fetchInvoiceDetails() {
      try {
        const response = await billingServer.getInvoice(this.invoiceId);
        this.invoice = response.data;
      } catch (error) {
        console.error(error);
      }
    }
  }
});
</script>

<style scoped>
.invoice-nav {
  background-color: #f5f6f9;
  display: flex;
  border-radius: 15px;
  height: 50px;
  justify-content: space-between;
  align-content: center;
  align-items: center;
}
.payment-method-container {
  padding: 15px;
  border-radius: 15px;
  margin-top: 10px;
}
</style>
